// AnswerActions — copy, ask again, and was-this-right, under one answer.
//
// ⚠️ ONE COMPONENT, BOTH SHELLS. The desktop page and the app's Ask screen are
// two skins over one engine, and the moment a control is written twice the two
// start drifting — which is already how the desktop ended up without the
// history rail the phone had. Anything added here appears in both places at
// once, in whatever palette the surface passes down.

import { useEffect, useState } from "react";
import { Copy, Check, RotateCcw, ThumbsUp, ThumbsDown, Share } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { answerToText } from "@/lib/consultantCopy";
import type { MessagePart } from "@/types/consultant";

export function AnswerActions({
  messageId,
  parts,
  onRegenerate,
  colour,
  className,
}: {
  messageId: string;
  parts: MessagePart[];
  /** Omitted on anything but the newest answer — see ConsultantChat. */
  onRegenerate?: () => void;
  /** Surface ink. Undefined lets the theme's muted foreground apply. */
  colour?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  // ⚠️ RESOLVED ONCE, IN AN EFFECT. Reading navigator.share during render makes
  // the control appear and vanish between paints on some browsers; resolving it
  // after mount means it either renders or does not.
  const [canShare, setCanShare] = useState(false);
  useEffect(() => {
    setCanShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [busy, setBusy] = useState(false);

  // ⚠️ Nothing renders when there is nothing to act on. answerToText is empty
  // for a message that is only follow-up chips, and a copy button that puts
  // nothing on the clipboard is worse than no button.
  const text = answerToText(parts);
  if (!text) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be refused outright — an insecure origin, a
      // locked-down browser. Leaving the icon unchanged is the honest signal.
      setCopied(false);
    }
  };

  const vote = async (next: "up" | "down") => {
    if (busy) return;
    // Pressing the same thumb clears it: an absent opinion and a neutral one
    // are different, and only one of them belongs in the review queue.
    const value = rating === next ? "clear" : next;
    const previous = rating;
    setBusy(true);
    setRating(value === "clear" ? null : next);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.rpc as any)("rpc_set_consultant_feedback", {
        p_message_id: messageId,
        p_rating: value,
        p_note: null,
      });
      if (error) throw error;
    } catch {
      // ⚠️ NEVER BLOCKS, NEVER SHOUTS. A failed vote reverts quietly; throwing a
      // banner over an answer someone is mid-way through costs more than the
      // vote is worth.
      setRating(previous);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Hand the answer to whatever the device shares with.
   *
   * ⚠️ IT SHARES THE SAME STRING THE CLIPBOARD GETS — citations included. An
   * answer texted to a partner or a lender without its sources is the artefact
   * this product exists not to produce, and a share sheet is exactly how that
   * would happen at scale.
   */
  const share = async () => {
    try {
      await navigator.share({ title: "RaisedBeef Cattle Consultant", text });
    } catch {
      // A cancelled share sheet rejects. That is a decision, not a failure, and
      // there is nothing to tell the reader either way.
    }
  };

  const btn = "inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-[11px] font-medium transition-opacity";
  const style = colour ? { color: colour } : undefined;

  return (
    <div className={cn("flex items-center gap-0.5", className)}>
      <button
        type="button" onClick={copy} style={style}
        aria-label={copied ? "Answer copied" : "Copy answer with sources"}
        title={copied ? "Copied" : "Copy answer with sources"}
        className={cn(btn, "opacity-55 hover:opacity-100")}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>

      {/* ⚠️ ONLY WHERE THE DEVICE HAS A SHARE SHEET. On a desktop browser without
          navigator.share this would fall back to copying — a second button doing
          what the first one already does, which is worse than not offering it. */}
      {canShare ? (
        <button
          type="button" onClick={() => void share()} style={style}
          aria-label="Share this answer" title="Share this answer"
          className={cn(btn, "opacity-55 hover:opacity-100")}
        >
          <Share className="h-3.5 w-3.5" />
          Share
        </button>
      ) : null}

      {onRegenerate ? (
        <button
          type="button" onClick={onRegenerate} style={style}
          aria-label="Ask this question again" title="Ask this question again"
          className={cn(btn, "opacity-55 hover:opacity-100")}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Ask again
        </button>
      ) : null}

      <span className="mx-0.5 h-3 w-px bg-current opacity-20" aria-hidden />

      <button
        type="button" onClick={() => void vote("up")} style={style}
        aria-label="This answer was helpful" aria-pressed={rating === "up"} title="Helpful"
        className={cn(btn, rating === "up" ? "opacity-100" : "opacity-45 hover:opacity-90")}
      >
        <ThumbsUp className={cn("h-3.5 w-3.5", rating === "up" && "fill-current")} />
      </button>
      <button
        type="button" onClick={() => void vote("down")} style={style}
        aria-label="This answer was wrong — send it for review"
        aria-pressed={rating === "down"} title="Wrong — send for review"
        className={cn(btn, rating === "down" ? "opacity-100" : "opacity-45 hover:opacity-90")}
      >
        <ThumbsDown className={cn("h-3.5 w-3.5", rating === "down" && "fill-current")} />
      </button>
    </div>
  );
}
